// assets/product-custom-property.js
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#bindVariantVisibility();

    if (this.refs.textInput && this.refs.characterCount) {
      this.#updateCharacterCount();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const target = this.#getVariantEventTarget();
    if (target) {
      target.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
      target.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelection);
    }

    const variantPicker = this.#getVariantPicker();
    variantPicker?.removeEventListener('change', this.#handleVariantSelection);
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  #bindVariantVisibility() {
    if (!this.#hasVisibilityRules()) return;

    const target = this.#getVariantEventTarget();
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelection);

    const variantPicker = this.#getVariantPicker();
    variantPicker?.addEventListener('change', this.#handleVariantSelection);

    this.#updateVisibility();
  }

  #getVariantEventTarget() {
    return this.closest('[id*="ProductInformation-"], featured-product-information, [id*="QuickAdd-"], product-card, .shopify-section, dialog');
  }

  #getVariantPicker() {
    const target = this.#getVariantEventTarget();
    return target?.querySelector('variant-picker') ?? null;
  }

  #handleVariantSelection = () => {
    this.#updateVisibility();
  };

  #handleVariantUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (event.detail.data.newProduct) {
      this.#getConfigElement().dataset.productId = event.detail.data.newProduct.id;
    }

    if (
      this.#getConfigElement().dataset.productId &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.#getConfigElement().dataset.productId
    ) {
      return;
    }

    this.#updateVisibility(event.detail.resource?.title ?? '');
  };

  #hasVisibilityRules() {
    return this.#parseVisibilityRules(this.#getConfigElement().dataset.visibilityOptionValues).length > 0 ||
      this.#parseVisibilityRules(this.#getConfigElement().dataset.visibilityVariantTitles).length > 0;
  }

  #getConfigElement() {
    return this.querySelector('[data-product-custom-property-config]') ?? this;
  }

  #parseVisibilityRules(value) {
    return (value || '')
      .split(/[\n,]/)
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);
  }

  #getSelectedOptionValues() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return [];

    const checkedInputs = Array.from(variantPicker.querySelectorAll('input[type="radio"]:checked'));
    const selectedOptions = Array.from(variantPicker.querySelectorAll('select option:checked'));

    return [...checkedInputs, ...selectedOptions]
      .map((option) => option.value.trim().toLowerCase())
      .filter(Boolean);
  }

  #getSelectedVariantTitle() {
    const variantPicker = this.#getVariantPicker();
    const variantJson = variantPicker?.querySelector('script[type="application/json"]')?.textContent;
    if (!variantJson) return '';

    try {
      const variant = JSON.parse(variantJson);
      return (variant?.title || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }

  #updateVisibility(currentVariantTitle = '') {
    const configElement = this.#getConfigElement();
    const optionRules = this.#parseVisibilityRules(configElement.dataset.visibilityOptionValues);
    const variantRules = this.#parseVisibilityRules(configElement.dataset.visibilityVariantTitles);

    if (optionRules.length === 0 && variantRules.length === 0) {
      this.#setVisibility(true);
      return;
    }

    const selectedOptionValues = this.#getSelectedOptionValues();
    const normalizedVariantTitle = (currentVariantTitle || this.#getSelectedVariantTitle()).trim().toLowerCase();

    const optionMatch =
      optionRules.length > 0 && optionRules.some((rule) => selectedOptionValues.includes(rule));
    const variantMatch =
      variantRules.length > 0 && normalizedVariantTitle !== '' && variantRules.includes(normalizedVariantTitle);

    this.#setVisibility(optionMatch || variantMatch);
  }

  #setVisibility(visible) {
    const configElement = this.#getConfigElement();

    this.hidden = !visible;
    this.toggleAttribute('aria-hidden', !visible);
    this.style.display = visible ? '' : 'none';
    configElement.hidden = !visible;
    configElement.toggleAttribute('aria-hidden', !visible);
    configElement.style.display = visible ? '' : 'none';

    const fields = configElement.querySelectorAll('input, textarea, select');
    fields.forEach((field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
        return;
      }

      if (!field.dataset.initialRequired) {
        field.dataset.initialRequired = field.required ? 'true' : 'false';
      }

      field.disabled = !visible;
      field.required = visible && field.dataset.initialRequired === 'true';
    });
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
